<template>
<div>
  <transition name="slide-up">
    <div v-if="innerHTML && !hasSearched" v-html="innerHTML"></div>
  </transition>
  <div class="mb-widget__search">
    <div class="styles">
      <div style="align-items:center; display:flex; font-size:12px; font-weight:700; text-transform:uppercase;">Styles:</div>
      <select v-model="selectedStyleId" class="style-select">
        <option v-for="(style, index) in styles" :key="index" :value="style.id">{{style.name}}</option>
      </select>
      <div class="style-pills">
        <button v-for="(style, index) in styles" :key="index" @click="changeStyle(style.id)" :class="{selected: style.id === selectedStyleId}">{{style.name}}</button>
      </div>
    </div>

    <div class="tabs">
      <div v-for="(specialSource, index) in specialSources" :key="index" :class="{selected: selectedSourceId === specialSource.id}" class="tab">
        <button @click="changeSource(specialSource.id)">{{specialSource.name}}</button>
      </div>

      <div class="more tab" :class="{open: moreDropdownOpen, selected: moreSourceIsSelected}">
        <button @click="openMoreDropdown()">{{moreDropdownText}} <i class="material-icons">arrow_drop_down</i></button>
        <div class="more-sources-dropdown">
          <div style="font-weight:500; margin-bottom:20px;">Select a source</div>

          <div class="sources-container">
            <button v-for="(source, index) in config.sources" :key="index" @click="changeSource(source.id)">{{source.name}}</button>
          </div>
        </div>
      </div>
    </div>

    <div class="input-wrapper" ref="searchWidget">
      <input type="text" ref="searchTerm" v-model="searchTerm" :placeholder="inputPlaceholderText" @keyup.enter="doSearch()"/>
      <button class="btn btn-raised bg-blue white" :disabled="isLoading" @click="doSearch()">
        <span>
          <i class="material-icons">search</i>
          <span class="search-btn-text">Search</span>
        </span>
      </button>
    </div>

    <div v-if="showFileUrlWarning" style="background:rgb(242, 222, 222); color:rgb(169, 68, 66); padding:10px;">
      We can't autocite a file on your computer, but if it's a PDF you can drag and drop it into the main tool.
    </div>
    <div v-if="showNoHttpWarning" style="background:rgb(242, 222, 222); color:rgb(169, 68, 66); padding:10px;">
      URLs must start with <strong>http://</strong> or <strong>https://</strong>
    </div>

    <div v-if="selectedSourceId && !hasSearched && !isLoading" style="text-align:center;">
      <div class="or-break">or</div>
      <router-link :to="{name: 'NewReferenceManual', params:{sourceId: selectedSourceId, styleId: selectedStyleId}}" class="btn btn-outline btn-inline">
        enter manually
      </router-link>
    </div>

    <TooltipLg v-if="!hasSearched" tooltipId="autociteSearchBox" category="intro" placement="left" :target="searchWidgetTooltipTarget" :animate="true" primary>
      <p><strong>👋 Start here!</strong> Cite websites, books, journals, and videos automatically -- just search with the title or an identifier.</p>
      <p style="font-size:12px; opacity:0.6;">Tip: use the ISBN, ISSN, DOI, or URL for the best accuracy.</p>
    </TooltipLg>
  </div>

  <div v-if="isLoading" style="height:200px; position:relative;">
    <Spinner />
  </div>

  <div v-if="hasSearched && !isLoading" class="mb-widget__search-results" ref="searchResults">
    <div v-if="searchResults.length === 0" class="no-results-found">No results found. Try again or <router-link :to="{name: 'NewReferenceManual', params:{sourceId: selectedSourceId, styleId: selectedStyleId}}" class="btn btn-outline btn-inline">enter manually</router-link></div>
    <div v-if="searchResults.length > 0">
      <ul ref="searchResults">
        <li v-for="(result, index) in searchResults" :key="index" class="result">
          <button @click="selectResult(result)">
            <ListReferenceDetailed :reference="result" />
          </button>
        </li>
      </ul>
      <div style="text-align:center;">
        <div class="or-break">or</div>
        <router-link :to="{name: 'NewReferenceManual', params:{sourceId: selectedSourceId, styleId: selectedStyleId}}" class="btn btn-outline btn-inline">
          enter manually
        </router-link>
      </div>
      <TooltipLg v-if="searchResults.length > 0" tooltipId="autociteSearchResults" category="intro" placement="left start" :target="searchResultsTooltipTarget" primary>
        <p>🙌 Now select one of the results we found.</p>
      </TooltipLg>
    </div>
  </div>
</div>
</template>

<script>
import Config from '@/config.js';
import AutociteApi from '@/services/autociteApi.js';
import ImportApi from '@/services/importApi.js';
import MessageBus from '@/services/messageBus.js';
import {styles} from '@/utils/styles.js';

import ListReferenceDetailed from '@/components/ListReferenceDetailed';
import TooltipLg from '@/components/TooltipLg';
import Spinner from '@/components/Spinner';

export default {
  name: 'NewReferenceSearch',
  components: {
    ListReferenceDetailed,
    TooltipLg,
    Spinner
  },
  props: ['innerHTML', 'styleId', 'styleName', 'sourceId', 'autoImport'],
  data () {
    return {
      config: Config,
      styles: styles,
      selectedStyleId: null,
      selectedSourceId: null,
      specialSources: [{
        id: 'webpage',
        name: '🌐 Website',
        inputPlaceholderText: 'Enter or paste a web address to cite. URLs to PDFs are supported.'
      }, {
        id: 'book',
        name: '📚 Book',
        inputPlaceholderText: 'Search for the book title or ISBN'
      }, {
        id: 'article_journal',
        name: '📜 Journal',
        inputPlaceholderText: 'Search for the journal article title or DOI'
      }, {
        id: 'video',
        name: '📹 Video',
        inputPlaceholderText: 'Search for the video title or paste a web address'
      }],
      moreDropdownOpen: false,
      searchTerm: '',
      hasSearched: false,
      isLoading: false,
      searchResults: [],
      searchWidgetTooltipTarget: null,
      searchResultsTooltipTarget: null
    }
  },
  computed: {
    selectedStyle () {
      return this.styles.find(s => s.id === this.selectedStyleId);
    },
    selectedSource () {
      return Config.sources.find((s) => { return s.id === this.selectedSourceId });
    },
    moreSourceIsSelected () {
      return this.selectedSourceId && !this.specialSources.find((s) => { return s.id === this.selectedSourceId });
    },
    moreDropdownText () {
      return this.moreSourceIsSelected ? this.selectedSource.name : 'More';
    },
    inputPlaceholderText () {
      return this.moreSourceIsSelected ? 'Search for the title or enter a URL' : this.selectedSourceId !== null ? this.specialSources.find((s) => { return s.id === this.selectedSourceId }).inputPlaceholderText : '';
    },
    showFileUrlWarning () {
      return this.searchTerm.toLowerCase().startsWith('file://');
    },
    showNoHttpWarning () {
      return this.selectedSourceId === 'webpage' && this.searchTerm && (!this.searchTerm.toLowerCase().startsWith('http://') && !this.searchTerm.toLowerCase().startsWith('https://'));
    }
  },
  methods: {
    changeStyle (styleId) {
      this.selectedStyleId = styleId;
      window.styleId = styleId; // also update the window object for repeated references
    },
    changeSource (sourceId) {
      this.hasSearched = false;
      this.searchResults.length = 0;
      this.selectedSourceId = sourceId;
      this.moreDropdownOpen = false;
      // if the source type doesn't have autocite then redirect straight to the manual form
      if (!this.selectedSource.hasAutocite) {
        this.$router.push({name: 'NewReferenceManual', params: {sourceId: this.selectedSourceId, styleId: this.selectedStyleId}});
      } else {
        this.$refs.searchTerm.focus();
      }
    },
    openMoreDropdown () {
      this.selectedSourceId = null;
      this.moreDropdownOpen = true;
    },
    async doSearch () {
      if (this.showFileUrlWarning || this.showNoHttpWarning) return; // don't proceed of they're using the file on their PC

      if (this.searchTerm && this.searchTerm.length > 1) {
        // window.scrollTo({top: this.$el.getBoundingClientRect().top, left: 0, behavior: 'smooth'});

        this.isLoading = true;
        // clear any previous results
        if (this.searchResults && this.searchResults.length > 0) {
          this.searchResults.length = 0;
        }

        let searchResults = await AutociteApi.search(this.searchTerm, this.selectedSourceId);
        if (searchResults && searchResults.length > 0) {
          this.searchResults = searchResults;
        }
        this.isLoading = false;
        this.hasSearched = true;
      }
    },
    async selectResult (result) {
      this.isLoading = true;
      this.searchResults.length = 0;
      const formattedReference = await AutociteApi.autociteMetadata(result.metadata, result.sourceId, this.selectedStyleId);
      if (this.autoImport) {
        const stashId = await ImportApi.stashReference(formattedReference);
        // then main app import screen
        const form = document.createElement('form');
        form.setAttribute('method', 'post');
        form.setAttribute('action', `/i/${stashId}?style=${this.selectedStyleId}`);
        document.body.appendChild(form);
        form.submit();
      } else {
        MessageBus.formattedReference = formattedReference;
        this.$router.push({ name: 'FormattedReference', params: { styleId: this.selectedStyleId } });
      }
    },
    restart () {
      this.hasSearched = false;
      this.searchResults.length = 0;
      this.searchTerm = '';
      this.$nextTick(() => {
        this.changeSource('webpage');
      })
    }
  },
  created () {
    this.$nextTick(() => {
      // preselected style could be one of the defaults, or an undetermined style
      if (this.styleId) {
        const styleIdExists = this.styles.some(s => s.id === this.styleId);
        if (!styleIdExists && this.styleName) {
          // a new style is being defined, so add it
          this.styles.splice(0, 0, { id: this.styleId, name: this.styleName });
          // and remove Harvard Australia to make room
          const harvardAustraliaStyle = this.styles.find(s => s.id === 'harvard-australia');
          this.styles.splice(this.styles.indexOf(harvardAustraliaStyle), 1);
        }
        this.selectedStyleId = this.styleId;
      } else {
        this.selectedStyleId = 'modern-language-association-8th-edition'
      }

      this.selectedSourceId = this.sourceId || 'webpage';
    })
  },
  updated () {
    this.searchWidgetTooltipTarget = this.$refs.searchWidget;
    this.searchResultsTooltipTarget = this.$refs.searchResults;
  }
}
</script>

<style lang="scss">
@import '../variables.scss';

.mb-widget {
  .mb-widget__search {
    .styles { display:flex; margin-bottom:10px;
      .style-select { flex:1; margin-left:10px; padding:4px; }
      .style-pills { border:1px solid $ui-grey; border-radius:6px; display:none; flex:1; margin-left:10px; padding:4px 10px 0; text-align:center;
        button { border-radius:100px; margin-bottom:4px; padding:2px 10px;
          &.selected { background:#2c3e50; color:#fff; }
        }
      }
    }
    .tabs { display:flex;
      .tab { border-radius:3px 3px 0 0; border-top:3px solid transparent; position:relative; width:20%;
        >button { align-items:center; display:flex; font-size: 13px; font-weight: 500; height:40px; justify-content:center; text-transform:uppercase; width:100%; }
        .border-cover { background:#ffffff; bottom:-2px; display:none; height:3px; left:0.5px; position:absolute; right:0.5px; z-index:6}
        &.selected { background:$ui-grey; font-weight:500;
          &:nth-child(1n) { border-color: $secondary-color;}
          &:nth-child(2n) { border-color: #ea4335;}
          &:nth-child(3n) { border-color: #fbbc05;}
          &:nth-child(4n) { border-color: $primary-color;}
          &:nth-child(5n) { border-color: #000000;}
        }
        &.more {
          &.open { font-weight:500;
            .more-sources-dropdown { display:block; font-weight:400; }
          }
          &.selected { width:40%; }
        }
        &:hover { font-weight:500;}
      }

      .more-sources-dropdown { background:#ffffff; border:1px solid #d5d8dc; border-radius:3px 0 3px 3px; box-shadow:0 2px 6px rgba(0,0,0,0.1); display:none; padding:15px; position:absolute; right:-1px; top:100%; width:516%; z-index:5;
        input { border:1px solid #d7dbe0; border-radius:3px; font-size:14px; margin:10px 0; padding:8px 12px; width:100%; }
        .sources-container {
          button { border-radius:3px; padding:10px; text-align:left; width:100%;
            &:hover { background: $ui-grey; font-weight:500; }
          }
        }
      }
    }
    .input-wrapper { border:10px solid $ui-grey; display:flex;
      input { border-radius:2px 0 0 2px; border:none; flex:1 0 0; padding:10px; }
      button { border-radius:0 2px 2px 0; font-weight:500; font-size:15px; justify-content: center;
        .material-icons { margin-right:0;}
        .search-btn-text { display:none; }
      }
    }
  }

  .mb-widget__search-results { max-height:calc(100vh - 360px); overflow-y:auto;
    .no-results-found { align-items: center; display:flex; justify-content: center; height:200px; }
    .result {
      >button { padding:0; width:100%; }
    }
  }

  .slide-up-leave-active { transition:max-height 0.3s cubic-bezier(0.5,0.25,0,1); }
  .slide-up-leave { max-height: 208px; overflow: hidden; }
  .slide-up-leave-to { overflow: hidden; max-height: 0; }

  @media screen and (min-width: 60em) {
    .mb-widget__search {
      .styles {
        .style-select { display:none; }
        .style-pills { display:block; }
      }
      .tabs {
        .more-sources-dropdown { width:638px;
          .sources-container { display:flex; flex-direction:column; flex-wrap: wrap; height:410px; width:33%;}
        }
      }
      .input-wrapper {
        button {
          .material-icons { margin-right:6px; }
          .search-btn-text { display:inline; }
        }
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/NewReferenceSearch.vue